import React, { useState } from 'react';
import { useInvalidate } from '@refinedev/core';
import { Button, Upload, message } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../providers/dataProvider';


interface ExcelButtonsProps {
  /** API resource path, e.g. "exercises", "badges". Used for both the export URL and cache invalidation. */
  resource: string;
  /** Filename for the downloaded file (without extension). Defaults to resource. */
  filename?: string;
  /** Display labels for import result keys. Defaults to "Imported/Updated". */
  resultLabels?: { imported?: string; updated?: string };
}

/**
 * Reusable Export/Import Excel buttons for CMS list pages.
 *
 * Usage:
 *   <ExcelButtons resource="badges" />
 */
export const ExcelButtons: React.FC<ExcelButtonsProps> = ({
  resource,
  filename,
  resultLabels,
}) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const invalidate = useInvalidate();

  const downloadName = filename || resource;
  const importedLabel = resultLabels?.imported || 'Imported';
  const updatedLabel = resultLabels?.updated || 'Updated';

  const handleExport = () => {
    setExporting(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE_URL}/${resource}/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${downloadName}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        message.success('Export downloaded');
      })
      .catch((err) => message.error(err.message || 'Export failed'))
      .finally(() => setExporting(false));
  };

  const handleImport = (file: File) => {
    setImporting(true);
    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);

    fetch(`${API_BASE_URL}/${resource}/import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) {
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        return body;
      })
      .then((result) => {
        const imported = result.imported ?? result.created ?? 0;
        const updated = result.updated ?? 0;
        const skipped = result.skipped ?? 0;
        const errors = result.errors ?? [];

        let summary = `${importedLabel}: ${imported}`;
        if (updated > 0) summary += `, ${updatedLabel}: ${updated}`;
        if (skipped > 0) summary += `, Skipped: ${skipped}`;

        if (errors.length > 0) {
          message.warning(`${summary}, Errors: ${errors.length}`);
        } else {
          message.success(summary);
        }
      })
      .catch((err) => message.error(err.message || 'Import failed'))
      .finally(() => {
        setImporting(false);
        invalidate({ resource, invalidates: ['list'] });
      });

    return false; // prevent antd Upload from auto-uploading
  };

  return (
    <>
      <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
        Export Excel
      </Button>
      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={handleImport}
      >
        <Button icon={<UploadOutlined />} loading={importing}>
          Import Excel
        </Button>
      </Upload>
    </>
  );
};
